<template>
  <select-account
    :parent-id="parentId"
    :account="modelValue"
    stretch
    @update:account="$emit('update:modelValue', $event); $emit('selected', $event)"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Account } from '@/iam/types';
import SelectAccount from '@/iam/account/SelectAccount.vue';
import accountRepository from '@/iam/account/account-repository';
import recognition from '@/auth/recognition';
import { RouteLocationNormalizedLoaded as Route } from 'vue-router';

export default defineComponent({
  emits: ['selected', 'update:modelValue'],
  components: {
    SelectAccount,
  },
  props: {
    modelValue: Object as () => Account,
  },
  data() {
    return {
      parentId: recognition.isServiceProvider() ? recognition.getAccountId() : null,
    };
  },
  computed: {
    accountId(): number|null {
      return this.modelValue ? this.modelValue.id : null;
    },
  },
  watch: {
    $route: {
      async handler(route: Route) {
        const accountIdStr = route.params.accountId;
        if (!accountIdStr) return;
        const accountId = parseInt(accountIdStr as string, 10);
        if (this.accountId === accountId) return;
        const account = await accountRepository.find(accountId);
        this.$emit('update:modelValue', account);
        this.$emit('selected', account);
      },
      immediate: true,
      deep: true,
    },
    modelValue(account: Account, prev: Account|null) {
      // This use of recognition temporary until all routes
      // have been updated to contain :accountId
      // TODO: REMOVE
      if (recognition.isCustomer()) return;
      if (this.$route.params.accountId === `${account.id}`) return;
      if (prev && this.canSubstitute(account, prev)) {
        this.$router.push({ params: { accountId: `${account.id}` } });
      } else {
        const path = `/admin/accounts/${account.id}/users`;
        this.$router.push(path);
      }
    },
  },
  methods: {
    async selectAccount(accountId: number) {
      const account = await accountRepository.find(accountId);
      this.$emit('update:modelValue', account);
      this.$emit('selected', account);
    },
    /*
      CAN SUBSTITUTE
      /dashboard/accounts/:accountId

      CANNOT SUBSTITUTE
      /social/accounts/:accountId/reports/:reportId
      The :reportId is specific to the :accountId so we
      can't just change the :accountId because the page will
      not load correctly.
     */
    canSubstitute(nextAccount: Account, prevAccount: Account): boolean {
      const paramsNames = Object.keys(this.$route.params);
      return (
        nextAccount.type === prevAccount.type
        && paramsNames.length === 1
        && paramsNames[0] === 'accountId'
      );
    },
  },
  created(): void {
    if (recognition.isCustomer() && recognition.getAccountId()) {
      this.selectAccount(recognition.getAccountId() as number);
    }
  },
});
</script>
